/**
 * Chat Flow Service
 * Handles all API communication with the backend
 */
export class ChatFlow {
  constructor(apiUrl) {
    this.apiUrl = apiUrl.replace(/\/$/, ''); // Remove trailing slash
  }

  async request(endpoint, options = {}) {
    const url = `${this.apiUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    };

    if (options.body) {
      config.body = JSON.stringify(options.body);
    }

    let response;
    try {
      response = await fetch(url, config);
    } catch (err) {
      const msg = err && err.message && (err.message === 'Failed to fetch' || err.message.includes('NetworkError'))
        ? 'Connection failed'
        : (err && err.message) || 'Connection failed';
      throw new Error(msg);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      const detail = error.detail || `HTTP ${response.status}`;
      throw new Error(detail);
    }

    return response.json();
  }

  async initChat() {
    // CRITICAL: NEVER pass session_token - backend always creates new session
    // Any old session_token is ignored by backend, but we don't send it
    return this.request('/api/chat/init', {
      method: 'POST',
      body: { session_token: null }  // Always null - backend ignores this anyway
    });
  }

  async identifyUser(userData) {
    return this.request('/api/chat/user/identify', {
      method: 'POST',
      body: userData
    });
  }

  async updateLanguage(sessionToken, language) {
    return this.request('/api/chat/language', {
      method: 'POST',
      body: {
        session_token: sessionToken,
        language: language
      }
    });
  }

  async getFAQs(sessionToken) {
    const response = await this.request(`/api/chat/faqs?session_token=${sessionToken}`, {
      method: 'GET'
    });
    return response.faqs || [];
  }

  async selectFAQ(sessionToken, faqId) {
    return this.request(`/api/chat/faq/select?session_token=${sessionToken}&faq_id=${faqId}`, {
      method: 'POST'
    });
  }

  async sendMessage(sessionToken, content) {
    return this.request('/api/chat/message', {
      method: 'POST',
      body: {
        session_token: sessionToken,
        content: content
      }
    });
  }

  async requestHuman(sessionToken) {
    return this.request(`/api/chat/human/request?session_token=${sessionToken}`, {
      method: 'POST'
    });
  }

  async cancelHuman(sessionToken) {
    // Cancel a pending human support request and return to AI fallback
    return this.request(`/api/chat/human/cancel?session_token=${sessionToken}`, {
      method: 'POST'
    });
  }

  async getChatHistory(sessionToken) {
    return this.request(`/api/chat/history?session_token=${sessionToken}`, {
      method: 'GET'
    });
  }
}

